/**
 * @fileoverview What a screen says when the photo help's allowance stops an AI request
 * (D23, marketing's words), on the screen where it happened: the photo tray,
 * the packet scan and the check-up.
 *
 *   - A subscriber's month: "You've reached this month's limit for
 *     {check-ups | photo identification | packet scans}. Some comes back on
 *     {date}. Everything else in Grow keeps working." The date is the
 *     server's, in the gardener's language and time zone: the allowance is
 *     rolling, so no reset day is ever promised.
 *   - The day: "That's a lot of photos for one day. The rest will be ready
 *     tomorrow."
 *   - A trial's: "Your trial's photo help is used up. Subscribe to Grow More
 *     for the full allowance, from $39 a year.", a link to the subscribe
 *     page, with the yearly price the subscribe page shows (Stripe's, through
 *     the billing status). A member tending someone else's garden is told
 *     whose plan it is instead (the server's tending line), with no link (useTendingFor; B23: the invitee only uses the inviter's plan).
 *
 * Where: the photo tray (PhotoTray), "Check and save" (TrayPiles) and
 * the plant check-up (PlantDoctor), under the button that was refused.
 * API: none of its own; the yearly price comes from GET /billing/status
 * (useBilling).
 * Words (en/es): serverErrors.AI_LIMIT_MONTH_PHOTO / _SCAN / _DOCTOR,
 * serverErrors.AI_LIMIT_DAY, serverErrors.AI_LIMIT_TRIAL (no price),
 * aiAllowance.trialUsedUp (with the price).
 * Watch out: the month's message names what stopped (the code carries the
 * feature) and the day some comes back; there is no reset day and no number
 * of uses anywhere.
 *
 * @module AiLimitNotice
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { formatPrice, useBilling, useTendingFor } from '../lib/useBilling';
import type { AiLimitRefusal } from '../lib/aiLimit';

/**
 * The words for one refusal, and Subscribe for a trial's.
 *
 * @param props.refusal - The refusal (aiLimitOf).
 * @param props.className - Extra classes for the box.
 * @param props.onLeave - Called as the trial's Subscribe link is followed (a sheet closes itself).
 */
export const AiLimitNotice: React.FC<{ refusal: AiLimitRefusal; className?: string; onLeave?: () => void }> = ({ refusal, className, onLeave }) => {
  const { reactTranslate: t } = useTranslation();
  const { data } = useBilling();
  /** Someone else's garden: the owner's plan, so no Subscribe (B23). */
  const tendingFor = useTendingFor();
  const box = cn('rounded-sm border bg-card px-3 py-2 text-sm text-muted-foreground', className);

  if (refusal.limit === 'trial' && tendingFor) {
    return <p role="status" className={box}>{tendingFor.line}</p>;
  }
  if (refusal.limit === 'trial') {
    // The yearly price as the subscribe page shows it; without one, the sentence without a price.
    const year = data?.plans.year;
    return (
      <p role="status" className={box}>
        <Link to="/subscribe" className="text-foreground underline underline-offset-2" onClick={onLeave}>
          {year ? t('aiAllowance.trialUsedUp', { price: formatPrice(year) }) : t('serverErrors.AI_LIMIT_TRIAL')}
        </Link>
      </p>
    );
  }
  return (
    <p role="status" className={box}>
      {t(`serverErrors.${refusal.code}`, { date: refusal.date ?? '' })}
    </p>
  );
};

/** The notice, as a default import. */
export default AiLimitNotice;
